// ABOUTME: The recap sentence of a bulk change action, e.g. "Set Tagline to “Silent” where it is empty, in English, Italian."
// ABOUTME: Pure: field, node, value and language labels come in, so the sentences are unit-testable for every kind.

import { IntlShape } from 'react-intl';

import { BulkChangeAction } from '../types';
import { isAllTextFields } from './actionDrafts';
import { storedValueText } from './valueDisplay';

export type ActionLabels = {
    formatMessage: IntlShape['formatMessage'];
    fieldLabel: (ctg: string, ct: string) => string;
    /** The label of a node id (link actions); the id itself when unknown. */
    nodeLabel: (id: string) => string;
    /** The label of a value of the field (a select option label); the value itself otherwise. */
    valueLabel: (ctg: string, ct: string, value: string) => string;
    /** The name of a language; the code itself when unknown. */
    languageLabel?: (code: string) => string;
    /** False for a non-localised field, which has one value for every language. */
    isLocalized?: (ctg: string, ct: string) => boolean;
    /** The company default language: the source of a translation. */
    defaultLanguage?: string;
};

const MISSING = '…';

export function describeAction(action: BulkChangeAction, labels: ActionLabels): string {
    const { formatMessage, nodeLabel } = labels;
    const field = (ctg: string, ct: string) => {
        if (isAllTextFields({ ctg, ct })) {
            return formatMessage({
                id: 'bulkChange.describe.allTextFields',
                defaultMessage: 'every text field',
            });
        }
        return ctg && ct ? labels.fieldLabel(ctg, ct) : MISSING;
    };
    const languageLabel = labels.languageLabel ?? ((code: string) => code);
    if (action.kind === 'deleteNode') {
        return formatMessage(
            {
                id: 'bulkChange.describe.deleteNode',
                defaultMessage:
                    'Delete the objects in scope with all their descendants; objects linking to them {linkedAction, select, cascade {are deleted too} other {are kept and unlinked}}.',
            },
            { linkedAction: action.linkedAction },
        );
    }
    // ", in every repetition of the group" for an action on every group index.
    const groups = action.allGroupIndexes
        ? `, ${formatMessage({
              id: 'bulkChange.describe.everyGroup',
              defaultMessage: 'in every repetition of the group',
          })}`
        : '';
    // ", in English" / ", in every language"; nothing for a non-localised field.
    const langList = (ctg: string, ct: string, langs: string[] | undefined) => {
        if (labels.isLocalized && !isAllTextFields({ ctg, ct }) && !labels.isLocalized(ctg, ct)) {
            return groups;
        }
        const phrase = langs?.length
            ? formatMessage(
                  { id: 'bulkChange.describe.inLanguages', defaultMessage: 'in {langs}' },
                  { langs: langs.map(languageLabel).join(', ') },
              )
            : formatMessage({
                  id: 'bulkChange.describe.everyLanguage',
                  defaultMessage: 'in every language',
              });
        return `, ${phrase}${groups}`;
    };
    const nodes = (ids: string[]) => (ids.length ? ids.map(nodeLabel).join('”, “') : MISSING);

    switch (action.kind) {
        case 'setValue':
            return formatMessage(
                {
                    id: 'bulkChange.describe.setValue',
                    defaultMessage:
                        'Set {field} to “{value}” {mode, select, onlyIfEmpty {where it is empty} other {overwriting existing values}}{langs}.',
                },
                {
                    field: field(action.ctg, action.ct),
                    value:
                        labels.valueLabel(action.ctg, action.ct, storedValueText(action.value)) ||
                        MISSING,
                    mode: action.writeMode,
                    langs: langList(action.ctg, action.ct, action.locales),
                },
            );
        case 'clearValue':
            return formatMessage(
                { id: 'bulkChange.describe.clearValue', defaultMessage: 'Clear {field}{langs}.' },
                {
                    field: field(action.ctg, action.ct),
                    langs: langList(action.ctg, action.ct, action.locales),
                },
            );
        case 'copyValue':
            return formatMessage(
                {
                    id: 'bulkChange.describe.copyValue',
                    defaultMessage:
                        'Copy {from} into {to}{mode, select, onlyIfEmpty { where it is empty} other {, overwriting}}{langs}.',
                },
                {
                    from: `${field(action.from.ctg, action.from.ct)}${
                        action.from.lang ? ` (${languageLabel(action.from.lang)})` : ''
                    }`,
                    to: field(action.ctg, action.ct),
                    mode: action.writeMode,
                    langs: langList(
                        action.ctg,
                        action.ct,
                        action.toLang ? [action.toLang] : action.locales,
                    ),
                },
            );
        case 'replaceText':
            return formatMessage(
                {
                    id: 'bulkChange.describe.replaceText',
                    defaultMessage: 'Replace “{find}” with “{replace}” in {field}{langs}.',
                },
                {
                    find: action.find || MISSING,
                    replace: action.replace,
                    field: field(action.ctg, action.ct),
                    langs: langList(action.ctg, action.ct, action.locales),
                },
            );
        case 'addLinks':
            return formatMessage(
                { id: 'bulkChange.describe.addLinks', defaultMessage: 'Add “{nodes}” to {field}.' },
                { nodes: nodes(action.nodeIds), field: field(action.ctg, action.ct) },
            );
        case 'removeLinks':
            return formatMessage(
                {
                    id: 'bulkChange.describe.removeLinks',
                    defaultMessage: 'Remove “{nodes}” from {field}.',
                },
                { nodes: nodes(action.nodeIds), field: field(action.ctg, action.ct) },
            );
        case 'translate':
            return formatMessage(
                {
                    id: 'bulkChange.describe.translate',
                    defaultMessage:
                        'Translate {field} from {source} into {langs}{mode, select, unapproved {, overwriting unapproved machine translations} all {, overwriting all translations} other { where empty}}{groups}.',
                },
                {
                    field: isAllTextFields(action)
                        ? formatMessage({
                              id: 'bulkChange.describe.allMultilingualFields',
                              defaultMessage: 'every multilingual field',
                          })
                        : field(action.ctg, action.ct),
                    source: labels.defaultLanguage
                        ? languageLabel(labels.defaultLanguage)
                        : MISSING,
                    langs: action.locales.length
                        ? action.locales.map(languageLabel).join(', ')
                        : MISSING,
                    mode: action.overwrite ?? 'onlyEmpty',
                    groups,
                },
            );
        default:
            return formatMessage(
                {
                    id: 'bulkChange.describe.replaceLink',
                    defaultMessage: 'Replace “{from}” with “{to}” in {field}.',
                },
                {
                    from: action.fromNodeId ? nodeLabel(action.fromNodeId) : MISSING,
                    to: action.toNodeId ? nodeLabel(action.toNodeId) : MISSING,
                    field: field(action.ctg, action.ct),
                },
            );
    }
}
